import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { backendConfig, apiUrl } from '../config/backendConfig';
import { Home, User, X, ArrowLeft, Calendar, Landmark, BookOpen, LayoutGrid, BarChart3, Users } from 'lucide-react';
import FlipCard from '../components/FlipCard.jsx';
import ImpastoPastelThumb from '../components/ImpastoPastelThumb';
import AccountSettingsModal from '../components/AccountSettingsModal';
import ManageSubscriptionModal from '../components/ManageSubscriptionModal';
import ArtistWall from './ArtistWall';
import ReactionNotifications from '../components/ReactionNotifications';
import ArtistSearchCombobox from '../components/ArtistSearchCombobox';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import '../styles/dashboardLanding.css';
import PremiumLoader from '../components/PremiumLoader';
import { useDeferredLoader } from '../hooks/useDeferredLoader';
import { getAvatarEmoji } from '../components/AvatarSelector';
import {
  canAccessArtistProfileCreation,
  contextAllowsEntitlement,
  shouldForceStudioOnboarding,
} from '../utils/studioAccess';
import { completeOnboarding } from '../services/studioOnboardingApi';
import { getDisplayName as getArtistDisplayName } from '../utils/consentUtils';
import { API_ENDPOINTS } from '../config/backendConfig';
import {
  fetchJourneyHeroSlides,
  readJourneySlidesSessionCache,
  persistJourneySlidesSessionCache,
  clearJourneySlidesSessionCache,
  fetchUserArtworksByUid,
} from '../utils/dashboardJourneyHero';

const isStudentUser = (u) => (u?.accountType || 'student') === 'student';

/** Session memory: Artist Corner already passed profile + onboarding checks this tab session (avoids full-screen loader on every return). */
const artistCornerProfileGate = new Map();

function readArtistCornerGate(uid) {
  if (!uid) return null;
  return artistCornerProfileGate.get(uid) || null;
}

function writeArtistCornerGate(uid, data) {
  if (!uid) return;
  artistCornerProfileGate.set(uid, { ok: true, ...data });
}

function profileNameHint(profileData) {
  if (!profileData) return '';
  return (profileData.name || profileData.nickname || '').trim();
}

// Local folder for My Art World images: place files in public/my-art-world/ (see README there).
const MY_ART_WORLD_IMAGE_BASE = '/my-art-world';

// Small cards: imageUrl points to public/my-art-world/{key}.jpg. Add files to show images; gradient used if missing.
// Bullets = card-specific journey/benefit (framework: journey, explore, learn, create).
/** Studio feature picker modes (artwork select → feature route) */
const STUDIO_PICKER_MODES = {
  'art-appreciation': {
    pickerTitle: 'Select an artwork for Art Appreciation',
    continueLabel: 'Start Art Appreciation',
  },
  'ways-of-seeing': {
    pickerTitle: 'Select an artwork for Ways of Seeing',
    continueLabel: 'Start Ways of Seeing',
  },
  reimagine: {
    pickerTitle: 'Select an artwork to Reimagine',
    continueLabel: 'Reimagine your artwork',
  },
};

function navigateStudioFeature(navigate, mode, artwork) {
  if (!artwork) return;
  const imageUrl = artwork.imageUrl || artwork.artworkUrl;
  if (mode === 'art-appreciation') {
    navigate('/art-appreciation/feedback', {
      state: {
        imageUrl,
        title: artwork.title || '',
        technique: artwork.technique,
        inspiration: artwork.inspiration,
        startAnalysis: true,
      },
    });
    return;
  }
  if (mode === 'ways-of-seeing') {
    navigate('/art-world/selection', { state: { artwork } });
    return;
  }
  if (mode === 'reimagine') {
    navigate('/reimagine-studio', { state: { artwork } });
  }
}

/** Artist Corner card order: Art History → studio trio → Upload → Gallery → … */
const FEATURES = [
  { key: 'learn-art-history', title: 'Learn Art History', short: 'Journey to artworks', long: 'Explore and learn from artworks around the world on key concepts.', bullets: ['Explore artworks from around the world', 'Tap spots for fun facts; compare two artworks', 'Try a short hands-on activity'], impastoVariant: 'learn-art-history', route: '/learn-art-history' },
  { key: 'art-appreciation', title: 'Art Appreciation', short: 'Discover your artwork', long: 'Discover what makes your artwork special with guided prompts and fun facts.', bullets: ['Guided prompts about your creation', 'Fun facts tailored to your art', 'Reflect on technique and inspiration'], impastoVariant: 'art-appreciation', studioPicker: 'art-appreciation' },
  { key: 'ways-of-seeing', title: 'Ways of Seeing', short: 'Explore style lenses', long: 'Explore your art through different style lenses and creative choices.', bullets: ['50+ art styles to explore', 'Cubism, Impressionism, and more', 'See your art through new eyes'], impastoVariant: 'ways-of-seeing', studioPicker: 'ways-of-seeing' },
  { key: 'reimagine-artwork', title: 'Reimagine Your Artwork', short: 'Transform your art', long: 'Tell Artmosphere what to change and see your artwork transform.', bullets: ['Describe the change you want', 'AI-powered reimagining', 'Save and revisit in Activity Dashboard'], impastoVariant: 'reimagine-artwork', studioPicker: 'reimagine' },
  { key: 'upload-artwork', title: 'Upload New Artwork', short: 'Add a new creation', long: 'Add a new piece of art to your gallery and share your creativity.', bullets: ['Pick a photo and add a title', 'Choose Private or Shared', 'Use in Art Appreciation, Ways of Seeing, or Reimagine'], imageUrl: `${MY_ART_WORLD_IMAGE_BASE}/upload-artwork.jpg`, openUpload: true },
  { key: 'your-gallery', title: 'Visit Your Gallery', short: 'See your saved artworks', long: 'Your personal art collection. Browse and enjoy everything you’ve saved.', bullets: ['Your uploaded artworks', 'Open Art Appreciation, Ways of Seeing, or Reimagine', 'Sort by book or filter'], imageUrl: `${MY_ART_WORLD_IMAGE_BASE}/your-gallery.jpg`, route: '/artist-wall' },
  {
    key: 'co-learning-museum',
    title: 'Artist Salon',
    short: 'A creative gathering space for artists',
    long: 'Daily activities, book galleries, art history, and Ways of Seeing — share your work and discover what others made in one warm studio.',
    bullets: ['Daily Art Studio & book walls', 'Art History co-learning', 'Ways of Seeing showcase'],
    imageUrl: `${MY_ART_WORLD_IMAGE_BASE}/learning-paths.jpg`,
    route: '/gallery/studio',
  },
  { key: 'visit-book-store', title: 'Visit Book Store', short: 'Browse & create books', long: 'Explore the Artmosphere collection and create your own books from your art and stories.', bullets: ['Browse and create your book', 'Add your art; order a printed book', 'View or edit in your collection'], imageUrl: `${MY_ART_WORLD_IMAGE_BASE}/visit-book-store.jpg`, route: '/create-books' },
  { key: 'activity-dashboard', title: 'Visit Activity Dashboard', short: 'Your activities in one place', long: 'See all the creative things you’ve done. Your activities in one place.', bullets: ['All your activities in one place', 'View details for each artwork', 'Revisit Art Appreciation, Ways of Seeing, and Reimagine'], imageUrl: `${MY_ART_WORLD_IMAGE_BASE}/activity-dashboard.jpg`, route: '/activity-dashboard' },
];

// Hero card (Art Activity of the Day): place hero.jpg in public/my-art-world/ to show it.
const DAILY_ACTIVITY_IMAGE_PATH = `${MY_ART_WORLD_IMAGE_BASE}/hero.jpg`;

/**
 * IMAGE SIZES FOR EXACT UPLOADS
 * — Hero card (Art Activity of the Day): flexible height; use at least 800×600 px or 1200×800 px. Aspect ~3:2 or 4:3.
 * — Small feature cards: use exactly 600×400 px (3:2 ratio). Images are object-cover so 600×400 fills the card without letterboxing.
 */
export const FEATURES_IMAGE_SIZE = { width: 600, height: 400 };

const DashboardLanding = () => {
  const { user, logout, membership, refreshMembership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const uid = user?.uid;
  const [openCardKey, setOpenCardKey] = useState(null);
  const [showStudioPicker, setShowStudioPicker] = useState(false);
  const [studioPickerMode, setStudioPickerMode] = useState(null);
  const [studioArtworks, setStudioArtworks] = useState([]);
  const [studioArtworksLoading, setStudioArtworksLoading] = useState(false);
  const [selectedStudioArtwork, setSelectedStudioArtwork] = useState(null);
  /** From Artist Wall “Magic Studio”: artwork already chosen — pick which feature */
  const [showPlayChoiceCards, setShowPlayChoiceCards] = useState(false);
  const [showArtistWallUploadOverlay, setShowArtistWallUploadOverlay] = useState(false);
  const [magicStudioImageFailed, setMagicStudioImageFailed] = useState(false);
  const [showActivityDashboardPicker, setShowActivityDashboardPicker] = useState(false);
  const mainRef = useRef(null);
  const [profileChecked, setProfileChecked] = useState(() => !!readArtistCornerGate(uid));
  const [artistProfilePicture, setArtistProfilePicture] = useState(null);
  const [artistAvatarId, setArtistAvatarId] = useState(null);
  const [artistDisplayName, setArtistDisplayName] = useState(() => readArtistCornerGate(uid)?.displayName || '');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountBtnRef = useRef(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  /** Journey hero: Level 1 art from artHistoryArtworks + optional user art; rotates every 2s */
  const [journeySlides, setJourneySlides] = useState([]);
  const [journeySlideIndex, setJourneySlideIndex] = useState(0);
  /** Only updated after the next URL has decoded — avoids blank flashes between slides */
  const [journeyHeroDisplayedSlide, setJourneyHeroDisplayedSlide] = useState(null);
  const journeyHeroDisplayedRef = useRef(null);
  const failedJourneySlideIdsRef = useRef(new Set());
  const journeySlidesRef = useRef([]);
  journeySlidesRef.current = journeySlides;
  const [showAdminLink, setShowAdminLink] = useState(false);
  const { usage, featureLimits, periodEnd, carryForward, loading: quotaLoading, refreshUsage } = useFeatureQuota();
  const [showQuotaStatus, setShowQuotaStatus] = useState(false);
  const didRefreshQuotaOnThisOpenRef = useRef(false);

  // Fetch quota usage ONCE when the quota modal opens.
  // After the first load, don't auto-refresh every 5s (prevents "loading loop" UX).
  useEffect(() => {
    if (!showQuotaStatus) {
      didRefreshQuotaOnThisOpenRef.current = false;
      return;
    }
    if (didRefreshQuotaOnThisOpenRef.current) return;
    didRefreshQuotaOnThisOpenRef.current = true;
    refreshUsage?.();
  }, [showQuotaStatus, refreshUsage]);

  useEffect(() => {
    if (!user) {
      setShowAdminLink(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${apiUrl('/api/admin/check-admin')}?uid=${user.uid}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setShowAdminLink(!!data.isAdmin);
      } catch (_) {
        if (!cancelled) setShowAdminLink(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Restore avatar + checked state before paint when returning to Artist Corner (session gate hit).
  useLayoutEffect(() => {
    if (!uid) {
      setProfileChecked(false);
      return;
    }
    const g = readArtistCornerGate(uid);
    if (g?.ok) {
      setProfileChecked(true);
      if (g.avatarId != null) setArtistAvatarId(g.avatarId);
      else if (g.profilePicture) setArtistProfilePicture(g.profilePicture);
      if (g.displayName) setArtistDisplayName(g.displayName);
    } else {
      setProfileChecked(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!user) {
      setProfileChecked(true);
      return;
    }
    if (readArtistCornerGate(user.uid)?.ok) {
      return;
    }
    const checkArtistProfile = async () => {
      try {
        // Existing artist profile is the strongest signal that onboarding is already done.
        // Check this first so transient membership/CORS failures never push active users back to onboarding.
        const profileSnap = await getDoc(doc(db, 'artistProfiles', user.uid));
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          const nameHint = profileNameHint(data);
          if (data?.avatarId) {
            setArtistAvatarId(data.avatarId);
          } else if (data?.profilePicture) {
            setArtistProfilePicture(data.profilePicture);
          }
          if (nameHint) setArtistDisplayName(nameHint);
          writeArtistCornerGate(user.uid, {
            avatarId: data?.avatarId ?? null,
            profilePicture: data?.profilePicture ?? null,
            displayName: nameHint || null,
          });
          setProfileChecked(true);
          return;
        }

        // Fast path: studio onboarding is driven by Firestore user flags. Do not wait on
        // /membership/status (can be slow or blocked) — that left "Loading your art world..." up for a long time.
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userDoc = userSnap.exists() ? userSnap.data() : {};

        // Join in-flight membership or use AuthContext snapshot before onboarding redirect.
        let gateEarly = membership;
        if (isStudentUser(user) && gateEarly == null) {
          try {
            gateEarly = await Promise.race([
              refreshMembership(user.uid, { force: false }),
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 4000)),
            ]);
          } catch (_) {
            gateEarly = null;
          }
        }

        if (isStudentUser(user) && contextAllowsEntitlement(gateEarly)) {
          if (shouldForceStudioOnboarding(gateEarly, userDoc)) {
            void completeOnboarding('artmo_limited').catch(() => {});
          }
          writeArtistCornerGate(user.uid, { displayName: null });
          setProfileChecked(true);
          return;
        }

        const firestoreSaysStudioDone =
          userDoc.onboardingCompleted === true || userDoc.needsStudioOnboarding === false;

        if (
          isStudentUser(user) &&
          gateEarly != null &&
          shouldForceStudioOnboarding(gateEarly, userDoc)
        ) {
          navigate('/onboarding/studio-access', { replace: true });
          return;
        }

        // Only trust a synthetic membership when Firestore explicitly cleared onboarding — not when flags are missing.

        // Join in-flight membership fetch or use cache first; bounded wait so validation stays fast.
        let gate = membership;
        if (isStudentUser(user) && gate == null) {
          const withTimeout = async (promise, ms) => {
            try {
              return await Promise.race([
                promise,
                new Promise((_, reject) => setTimeout(() => reject(new Error('membership_timeout')), ms)),
              ]);
            } catch {
              return null;
            }
          };
          try {
            gate = await withTimeout(refreshMembership(user.uid, { force: false }), 5000);
            if (gate == null) {
              gate = await withTimeout(refreshMembership(user.uid, { force: true }), 6000);
            }
          } catch (_) {
            gate = null;
          }
          if (gate == null) {
            // Firestore already says onboarding is finished — do not send users back to the wizard when
            // /membership/status is slow or fails (same class of bug as isNewAccount + empty membership).
            if (isStudentUser(user) && firestoreSaysStudioDone) {
              gate = {
                onboardingCompleted: true,
                needsStudioOnboarding: false,
              };
            } else if (shouldForceStudioOnboarding(gate, userDoc)) {
              navigate('/onboarding/studio-access', { replace: true });
              return;
            }
          }
        }
        if (!profileSnap.exists()) {
          if (shouldForceStudioOnboarding(gate, userDoc)) {
            navigate('/onboarding/studio-access', { replace: true });
            return;
          }
          if (!canAccessArtistProfileCreation(gate)) {
            navigate('/onboarding/studio-access', { replace: true });
            return;
          }
          navigate('/artist-profile', { replace: true });
          return;
        }
        const data = profileSnap.data();
        const nameHint = profileNameHint(data);
        if (data?.avatarId) {
          setArtistAvatarId(data.avatarId);
        } else if (data?.profilePicture) {
          setArtistProfilePicture(data.profilePicture);
        }
        if (nameHint) setArtistDisplayName(nameHint);
        writeArtistCornerGate(user.uid, {
          avatarId: data?.avatarId ?? null,
          profilePicture: data?.profilePicture ?? null,
          displayName: nameHint || null,
        });
      } catch (err) {
        console.error('[DashboardLanding] Error checking artist profile:', err);
        if (isStudentUser(user)) {
          navigate('/onboarding/studio-access', { replace: true });
        } else {
          navigate('/artist-profile', { replace: true });
        }
        return;
      }
      setProfileChecked(true);
    };
    checkArtistProfile();
  }, [user, navigate, refreshMembership]);

  useEffect(() => {
    if (!user?.uid || !profileChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const name = await getArtistDisplayName(user.uid);
        if (cancelled || !name || name === 'Artist') return;
        setArtistDisplayName(name);
        const g = readArtistCornerGate(user.uid);
        if (g?.ok) writeArtistCornerGate(user.uid, { ...g, displayName: name });
      } catch (_) {
        /* keep profile-derived name */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, profileChecked]);

  // Fresh profile after saving from Artist Profile (first-time or edit)
  useEffect(() => {
    if (!location.state?.profileUpdated || !user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const profileSnap = await getDoc(doc(db, 'artistProfiles', user.uid));
        if (cancelled || !profileSnap.exists()) return;
        const data = profileSnap.data();
        const nameHint = profileNameHint(data);
        if (data?.avatarId) setArtistAvatarId(data.avatarId);
        else setArtistAvatarId(null);
        if (data?.profilePicture) setArtistProfilePicture(data.profilePicture);
        else setArtistProfilePicture(null);
        if (nameHint) setArtistDisplayName(nameHint);
        writeArtistCornerGate(user.uid, {
          avatarId: data?.avatarId ?? null,
          profilePicture: data?.profilePicture ?? null,
          displayName: nameHint || null,
        });
        setProfileChecked(true);
      } catch (_) {
        /* keep existing corner state */
      }
      if (!cancelled) navigate('.', { replace: true, state: {} });
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state?.profileUpdated, user?.uid, navigate]);

  // Artist Wall “Magic Studio” → pick feature for a preselected artwork (legacy entry)
  useEffect(() => {
    const preselected = location.state?.preselectedArtwork;
    const openPicker = location.state?.openStudioPicker || location.state?.openMagicPicker;
    const modeFromNav = location.state?.studioPickerMode || null;
    if (!openPicker) return;

    setShowStudioPicker(true);
    setStudioPickerMode(modeFromNav);
    if (preselected && (preselected.imageUrl || preselected.artworkUrl)) {
      setSelectedStudioArtwork({
        id: preselected.id,
        title: preselected.title || preselected.artworkTitle || 'Artwork',
        imageUrl: preselected.imageUrl || preselected.artworkUrl,
        technique: preselected.technique,
        inspiration: preselected.inspiration,
      });
      setMagicStudioImageFailed(false);
      setShowPlayChoiceCards(!modeFromNav);
    } else {
      setSelectedStudioArtwork(null);
      setMagicStudioImageFailed(false);
      setShowPlayChoiceCards(false);
    }
    navigate('/my-art-world', { replace: true, state: {} });
  }, [
    location.state?.openStudioPicker,
    location.state?.openMagicPicker,
    location.state?.studioPickerMode,
    location.state?.preselectedArtwork,
    navigate,
  ]);

  useLayoutEffect(() => {
    if (!showAccountDropdown) {
      setAccountMenuAnchor(null);
      return undefined;
    }
    const update = () => {
      const el = accountBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAccountMenuAnchor({
        top: r.bottom + 8,
        right: Math.max(8, window.innerWidth - r.right),
        minWidth: Math.max(192, r.width),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showAccountDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showAccountDropdown) return;
      if (e.target.closest('.dashboard-account-dropdown')) return;
      if (e.target.closest('[data-dashboard-account-menu]')) return;
      setShowAccountDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountDropdown]);

  useEffect(() => {
    const closeMenus = () => setShowAccountDropdown(false);
    window.addEventListener('ui-close-popovers', closeMenus);
    return () => window.removeEventListener('ui-close-popovers', closeMenus);
  }, []);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleEnter = (featureKey) => {
    const feature = FEATURES.find((f) => f.key === featureKey);
    if (!feature) return;

    // Open Activity Dashboard picker modal with 4 windows
    if (featureKey === 'activity-dashboard') {
      setOpenCardKey(null);
      setShowActivityDashboardPicker(true);
      return;
    }

    if (feature.studioPicker) {
      setOpenCardKey(null);
      setStudioPickerMode(feature.studioPicker);
      setShowStudioPicker(true);
      setSelectedStudioArtwork(null);
      setShowPlayChoiceCards(false);
      return;
    }
    if (feature.openUpload) {
      setShowActivityDashboardPicker(false);
      setShowStudioPicker(false);
      setStudioPickerMode(null);
      setShowPlayChoiceCards(false);
      setShowArtistWallUploadOverlay(true);
      return;
    }
    if (feature.route) {
      if (featureKey === 'learn-art-history') {
        import('../utils/learnArtHistoryCache').then(({ warmLearnArtHistoryLanding }) => {
          try {
            warmLearnArtHistoryLanding();
          } catch (_) {}
        });
      }
      navigate(feature.route);
    }
  };

  const fetchUserArtworks = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    return fetchUserArtworksByUid(uid);
  };

  useLayoutEffect(() => {
    if (!user?.uid) {
      setJourneySlides([]);
      journeyHeroDisplayedRef.current = null;
      setJourneyHeroDisplayedSlide(null);
      return;
    }
    const cached = readJourneySlidesSessionCache(user.uid);
    if (cached.length) {
      setJourneySlides(cached);
      setJourneySlideIndex(0);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setJourneySlides([]);
      journeyHeroDisplayedRef.current = null;
      setJourneyHeroDisplayedSlide(null);
      return;
    }
    const uid = user.uid;
    const cached = readJourneySlidesSessionCache(uid);
    let cancelled = false;
    fetchJourneyHeroSlides(uid)
      .then((slides) => {
        if (cancelled) return;
        failedJourneySlideIdsRef.current = new Set();
        if (slides.length === 0) {
          setJourneySlides([]);
          journeyHeroDisplayedRef.current = null;
          setJourneyHeroDisplayedSlide(null);
          clearJourneySlidesSessionCache(uid);
        } else {
          setJourneySlides(slides);
          setJourneySlideIndex(0);
          journeyHeroDisplayedRef.current = null;
          setJourneyHeroDisplayedSlide(null);
          persistJourneySlidesSessionCache(uid, slides);
        }
      })
      .catch(() => {
        if (!cancelled && !cached.length) {
          setJourneySlides([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!journeySlides.length) {
      journeyHeroDisplayedRef.current = null;
      setJourneyHeroDisplayedSlide(null);
      return undefined;
    }
    const idx = journeySlideIndex % journeySlides.length;
    let target = journeySlides[idx];
    if (failedJourneySlideIdsRef.current.has(target.id)) {
      const nextValid = journeySlides.find((s) => !failedJourneySlideIdsRef.current.has(s.id));
      if (!nextValid) {
        journeyHeroDisplayedRef.current = null;
        setJourneyHeroDisplayedSlide(null);
        return undefined;
      }
      target = nextValid;
    }
    if (journeyHeroDisplayedRef.current?.id === target.id) {
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      if (cancelled) return;
      journeyHeroDisplayedRef.current = target;
      setJourneyHeroDisplayedSlide(target);
    };
    img.onerror = () => {
      if (cancelled) return;
      failedJourneySlideIdsRef.current.add(target.id);
      const slides = journeySlidesRef.current;
      const hasValid = slides.some((s) => !failedJourneySlideIdsRef.current.has(s.id));
      if (!hasValid) {
        journeyHeroDisplayedRef.current = null;
        setJourneyHeroDisplayedSlide(null);
        return;
      }
      setJourneySlideIndex((i) => (i + 1) % slides.length);
    };
    img.src = target.url;
    return () => {
      cancelled = true;
    };
  }, [journeySlideIndex, journeySlides]);

  useEffect(() => {
    if (journeySlides.length < 2) return undefined;
    const nextIdx = (journeySlideIndex + 1) % journeySlides.length;
    const next = journeySlides[nextIdx];
    const warm = new Image();
    warm.decoding = 'async';
    warm.src = next.url;
    return undefined;
  }, [journeySlideIndex, journeySlides]);

  useEffect(() => {
    if (journeySlides.length < 2) return undefined;
    const id = setInterval(() => {
      setJourneySlideIndex((i) => {
        const slides = journeySlidesRef.current;
        const len = slides.length;
        if (len < 2) return 0;
        for (let step = 1; step <= len; step++) {
          const candidate = slides[(i + step) % len];
          if (!failedJourneySlideIdsRef.current.has(candidate.id)) {
            return (i + step) % len;
          }
        }
        return i;
      });
    }, 2000);
    return () => clearInterval(id);
  }, [journeySlides.length]);

  useEffect(() => {
    if (!showStudioPicker || !user) return;
    setStudioArtworksLoading(true);
    fetchUserArtworks()
      .then(setStudioArtworks)
      .finally(() => setStudioArtworksLoading(false));
  }, [showStudioPicker, user?.uid]);

  const closeStudioPicker = () => {
    setShowStudioPicker(false);
    setStudioPickerMode(null);
    setShowPlayChoiceCards(false);
    setSelectedStudioArtwork(null);
  };

  const continueStudioPicker = () => {
    if (!selectedStudioArtwork || !studioPickerMode) return;
    const art = selectedStudioArtwork;
    closeStudioPicker();
    navigateStudioFeature(navigate, studioPickerMode, art);
  };

  const handleTryNow = () => {
    navigate('/daily-art-activity');
  };

  const goExploreArtists = () => {
    const q = artistSearchQuery.trim();
    navigate(q ? `/artists?search=${encodeURIComponent(q)}` : '/artists');
  };

  const nonHeroFeatures = FEATURES.filter((f) => f.key !== 'learn-art-history');
  const studioPickerCopy = studioPickerMode ? STUDIO_PICKER_MODES[studioPickerMode] : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openCardKey == null) return;
      const insideCard = e.target.closest('.flip-card-wrapper');
      if (!insideCard) setOpenCardKey(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCardKey]);

  const showPrepareLoader = useDeferredLoader(!!user && !profileChecked, { delayMs: 420 });

  if (!user) {
    return (
      <div className="artist-corner-page min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <div className="text-center text-gray-800 text-base font-semibold">
          Your session is not active.
        </div>
        <p className="text-center text-gray-600 text-sm max-w-md">
          Please sign in again to open Artist Corner.
        </p>
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="art-btn-primary px-5 py-2.5 rounded-full font-semibold"
        >
          Go to Home
        </button>
      </div>
    );
  }

  if (!profileChecked) {
    return (
      <>
        {showPrepareLoader ? (
          <PremiumLoader
            variant="overlay"
            message="Opening Artist Corner…"
            subtitle="Your creative studio is almost ready"
          />
        ) : null}
        <div className="artist-corner-page min-h-screen" aria-hidden="true" />
      </>
    );
  }

  return (
      <div className="artist-corner-page artm-page artm-page-pad art-content-reveal min-h-screen flex flex-col">
      {showArtistWallUploadOverlay && typeof document !== 'undefined' && createPortal(
        <ArtistWall
          forceOpenUpload
          onCloseUploadModal={() => setShowArtistWallUploadOverlay(false)}
        />,
        document.body
      )}
      {/* Activity Dashboard picker — portaled so fixed overlay is not trapped by route transition */}
      {showActivityDashboardPicker && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[200100] flex items-center justify-center bg-[#2f251b]/35 p-2 backdrop-blur-sm sm:p-4"
          onClick={() => setShowActivityDashboardPicker(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="activity-dashboard-picker-title"
        >
          <div
            className="artist-corner-modal-shell w-full max-w-4xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col overflow-hidden rounded-[22px] sm:rounded-[28px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="artist-corner-modal-header flex shrink-0 items-center justify-between px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4">
              <h2
                id="activity-dashboard-picker-title"
                className="art-premium-heading text-xl tracking-tight md:text-2xl"
                style={{ fontFamily: 'Baloo 2, system-ui, -apple-system, BlinkMacSystemFont, sans-serif' }}
              >
                Visit Activity Dashboard
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowQuotaStatus(true); refreshUsage?.(); }}
                  className="art-btn-secondary flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors"
                >
                  <BarChart3 className="w-4 h-4" strokeWidth={2} />
                  Quota available
                </button>
                <button
                  type="button"
                  onClick={() => setShowActivityDashboardPicker(false)}
                  className="auth-premium-close rounded-full p-2.5 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="artist-corner-modal-body flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 md:px-8 pt-4 sm:pt-5 pb-6 sm:pb-8">
              <p className="art-premium-body-text mb-6 text-sm font-medium md:text-base">
                What would you like to explore today? Pick a window to see your activities.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {/* Daily Activities */}
                <button
                  type="button"
                  onClick={() => {
                    setShowActivityDashboardPicker(false);
                    navigate('/daily-activities');
                  }}
                  className="artist-corner-picker-card group text-left rounded-2xl p-5 md:p-6"
                >
                  <div className="artist-corner-picker-icon mb-3 flex h-11 w-11 items-center justify-center rounded-xl transition-colors">
                    <Calendar className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="art-premium-heading mb-1.5 text-base tracking-tight md:text-lg">
                    Daily Activity Dashboard
                  </h3>
                  <p className="art-premium-body-text text-sm leading-snug">
                    See your Act of Art for the Day and all daily activities.
                  </p>
                </button>

                {/* Magic Studio */}
                <button
                  type="button"
                  onClick={() => {
                    setShowActivityDashboardPicker(false);
                    navigate('/activity-dashboard', { state: { view: 'magic-studio' } });
                  }}
                  className="artist-corner-picker-card group text-left rounded-2xl p-5 md:p-6"
                >
                  <div className="artist-corner-picker-icon mb-3 flex h-11 w-11 items-center justify-center rounded-xl transition-colors">
                    <LayoutGrid className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="art-premium-heading mb-1.5 text-base tracking-tight md:text-lg">
                    Magic Studio Activities
                  </h3>
                  <p className="art-premium-body-text text-sm leading-snug">
                    Open your Magic Studio activity window to revisit journeys and reimagined artworks.
                  </p>
                </button>

                {/* Art History Library */}
                <button
                  type="button"
                  onClick={() => {
                    setShowActivityDashboardPicker(false);
                    navigate('/my-art-history');
                  }}
                  className="artist-corner-picker-card group text-left rounded-2xl p-5 md:p-6"
                >
                  <div className="artist-corner-picker-icon mb-3 flex h-11 w-11 items-center justify-center rounded-xl transition-colors">
                    <Landmark className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="art-premium-heading mb-1.5 text-base tracking-tight md:text-lg">
                    Art History Library
                  </h3>
                  <p className="art-premium-body-text text-sm leading-snug">
                    Jump into your Learn Art History window and see what you&apos;ve explored.
                  </p>
                  {!quotaLoading && featureLimits?.artHistoryAppreciation?.enabled && (
                    <p className="mt-2 text-xs font-medium text-[#8b6914]">
                      {featureLimits.artHistoryAppreciation.limit === 'unlimited'
                        ? `${usage?.artHistoryAppreciation ?? 0} artworks explored`
                        : (() => {
                            const lim = featureLimits.artHistoryAppreciation.effectiveLimit ?? featureLimits.artHistoryAppreciation.limit;
                            const carried = carryForward?.artHistoryAppreciation ?? 0;
                            return `${usage?.artHistoryAppreciation ?? 0} / ${lim} artworks${carried > 0 ? ` (+${carried} carried)` : ''}`;
                          })()}
                    </p>
                  )}
                </button>

                {/* Once Upon a Click */}
                <button
                  type="button"
                  onClick={() => {
                    setShowActivityDashboardPicker(false);
                    navigate('/my-art-stories');
                  }}
                  className="artist-corner-picker-card group text-left rounded-2xl p-5 md:p-6"
                >
                  <div className="artist-corner-picker-icon mb-3 flex h-11 w-11 items-center justify-center rounded-xl transition-colors">
                    <BookOpen className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="art-premium-heading mb-1.5 text-base tracking-tight md:text-lg">
                    Once Upon a Click
                  </h3>
                  <p className="art-premium-body-text text-sm leading-snug">
                    Open your story activity window to see stories and art journeys.
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Quota available modal (opened from Visit Activity Dashboard) */}
      {showQuotaStatus && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[200110] flex items-center justify-center bg-[#2f251b]/35 p-4 backdrop-blur-sm"
          onClick={() => setShowQuotaStatus(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="quota-status-title"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md border-2 border-amber-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100 bg-amber-50/80">
              <h3 id="quota-status-title" className="text-lg font-semibold text-amber-900">
                Quota available
              </h3>
              <button
                type="button"
                onClick={() => setShowQuotaStatus(false)}
                className="p-2 rounded-full hover:bg-amber-100 text-amber-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              {quotaLoading ? (
                <p className="text-sm text-amber-700">Loading...</p>
              ) : (
                <>
                  <ul className="space-y-3">
                    {['artHistoryAppreciation', 'styleTransfer', 'dailyActivities', 'individualArtAppreciation', 'storyboarding'].map((key) => {
                      const config = featureLimits?.[key];
                      if (!config || !config.enabled) return null;
                      const used = usage?.[key] ?? 0;
                      const limit = config.effectiveLimit !== undefined && config.effectiveLimit !== null ? config.effectiveLimit : config.limit;
                      const isUnlimited = limit === 'unlimited';
                      const carried = carryForward?.[key] ?? 0;
                      const labels = {
                        artHistoryAppreciation: 'Art History (artworks)',
                        styleTransfer: 'Style Transfer (artworks)',
                        dailyActivities: 'Daily Activities (feedback)',
                        individualArtAppreciation: 'Individual Art Appreciation',
                        storyboarding: 'Storyboarding',
                      };
                      return (
                        <li key={key} className="flex items-center justify-between text-sm border-b border-amber-100 pb-2 last:border-0 last:pb-0">
                          <span className="text-amber-900 font-medium">{labels[key] || key}</span>
                          <span className="text-amber-700 tabular-nums">
                            {isUnlimited ? `${used} used (Unlimited)` : carried > 0 ? `${used} / ${limit} (+${carried} carried)` : `${used} / ${limit}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {!periodEnd && (usage && Object.keys(usage).length === 0) && (
                    <p className="text-xs text-amber-600 mt-4">
                      Complete membership to start tracking your usage.
                    </p>
                  )}
                  {periodEnd && (
                    <p className="text-xs text-amber-600 mt-4">
                      Resets: {typeof periodEnd?.toDate === 'function' ? periodEnd.toDate().toLocaleDateString() : new Date(periodEnd).toLocaleDateString()}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Studio feature picker (from hub cards or Artist Wall) */}
      {showStudioPicker && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[200100] flex items-center justify-center bg-[#2f251b]/35 p-4 backdrop-blur-sm"
          onClick={closeStudioPicker}
          role="dialog"
          aria-modal="true"
          aria-labelledby={showPlayChoiceCards ? 'play-choice-title' : 'studio-picker-title'}
        >
          <div
            className={`bg-white rounded-3xl shadow-2xl w-full max-h-[90vh] flex flex-col border-2 border-amber-200 ${showPlayChoiceCards ? 'max-w-4xl' : 'max-w-2xl'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {showPlayChoiceCards ? (
              /* Step 2: What kind of play? — kid-friendly 3 thumbnails, sophisticated */
              <>
                <div className="flex items-center justify-between p-4 pb-2 border-b border-amber-100/80">
                  <button
                    type="button"
                    onClick={() => setShowPlayChoiceCards(false)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-full px-3 py-2 transition-colors"
                    aria-label="Choose another artwork"
                  >
                    <ArrowLeft className="w-4 h-4" /> Choose another artwork
                  </button>
                  <button
                    type="button"
                    onClick={() => { closeStudioPicker(); }}
                    className="p-2 rounded-full hover:bg-amber-100 text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5 flex-1 min-h-0 overflow-auto">
                  <h2 id="play-choice-title" className="text-center text-xl md:text-2xl font-bold text-gray-800 mb-1" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    Awesome selection!
                  </h2>
                  <p className="text-center text-sm text-amber-700/90 mb-4">What kind of play are you interested in today? Pick one to start your adventure!</p>
                  <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-stretch sm:items-start">
                    {/* Left: selected artwork — fixed height; label + image so image top can align with thumbnails */}
                    <div className="flex-shrink-0 w-full sm:w-[200px] md:w-[240px] flex flex-col">
                      <div className="relative rounded-2xl border-2 border-amber-200 overflow-hidden bg-amber-50/50 w-full h-[220px] sm:h-[240px] md:h-[280px] flex-shrink-0">
                        <p className="absolute top-2 left-3 z-10 text-[11px] font-semibold text-amber-900 bg-white/65 backdrop-blur-sm px-2 py-0.5 rounded-full">
                          Your artwork
                        </p>
                        {selectedStudioArtwork?.imageUrl && !magicStudioImageFailed ? (
                          <img
                            src={selectedStudioArtwork.imageUrl}
                            alt={selectedStudioArtwork.title || 'Selected artwork'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.currentTarget;
                              try {
                                const baseSrc = selectedStudioArtwork?.imageUrl || '';
                                if (!baseSrc || img.dataset?.proxyTried === 'true') {
                                  img.style.display = 'none';
                                  setMagicStudioImageFailed(true);
                                  return;
                                }
                                img.dataset.proxyTried = 'true';
                                img.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(baseSrc)}`;
                              } catch (_) {
                                img.style.display = 'none';
                                setMagicStudioImageFailed(true);
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-amber-600 text-sm">
                            No image
                          </div>
                        )}
                      </div>
                      {selectedStudioArtwork?.title && (
                        <p className="text-xs text-gray-600 mt-2 line-clamp-2">{selectedStudioArtwork.title}</p>
                      )}
                    </div>
                    {/* Right: three play thumbnails — same height as artwork; top aligned with artwork (pt matches "Your artwork" label + mb) */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 min-w-0 sm:h-[240px] md:h-[280px]">
                      {/* Art appreciation — thumbnail card */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedStudioArtwork) return;
                          const art = selectedStudioArtwork;
                          closeStudioPicker();
                          navigateStudioFeature(navigate, 'art-appreciation', art);
                        }}
                        className="group relative flex flex-col h-full rounded-2xl border-2 border-amber-200/80 overflow-hidden hover:border-amber-400 hover:shadow-lg hover:shadow-amber-200/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-left"
                      >
                        <ImpastoPastelThumb variant="art-appreciation" />
                        <div className="relative z-10 flex-1 min-h-0 m-2 p-3 flex flex-col justify-end rounded-xl bg-white/80 backdrop-blur-[2px]">
                          <h3 className="font-bold text-gray-900 text-sm text-center">Art appreciation</h3>
                          <p className="text-[9px] text-gray-600 mt-1 leading-snug text-center whitespace-normal break-words">
                            Discover what makes your artwork special with guided prompts.
                          </p>
                        </div>
                      </button>
                      {/* Ways of seeing — thumbnail card */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedStudioArtwork) return;
                          const art = selectedStudioArtwork;
                          closeStudioPicker();
                          navigateStudioFeature(navigate, 'ways-of-seeing', art);
                        }}
                        className="group relative flex flex-col h-full rounded-2xl border-2 border-amber-200/80 overflow-hidden hover:border-amber-400 hover:shadow-lg hover:shadow-amber-200/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-left"
                      >
                        <ImpastoPastelThumb variant="ways-of-seeing" />
                        <div className="relative z-10 flex-1 min-h-0 m-2 p-3 flex flex-col justify-end rounded-xl bg-white/80 backdrop-blur-[2px]">
                          <h3 className="font-bold text-gray-900 text-sm text-center">Ways of seeing</h3>
                          <p className="text-[9px] text-gray-600 mt-1 leading-snug text-center whitespace-normal break-words">
                            Explore your art through different style lenses and quick choices.
                          </p>
                        </div>
                      </button>
                      {/* Reimagining your artwork — thumbnail card */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedStudioArtwork) return;
                          const art = selectedStudioArtwork;
                          closeStudioPicker();
                          navigateStudioFeature(navigate, 'reimagine', art);
                        }}
                        className="group relative flex flex-col h-full rounded-2xl border-2 border-amber-200/80 overflow-hidden hover:border-amber-400 hover:shadow-lg hover:shadow-amber-200/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 text-left"
                      >
                        <ImpastoPastelThumb variant="reimagine-artwork" />
                        <div className="relative z-10 flex-1 min-h-0 m-2 p-3 flex flex-col justify-end rounded-xl bg-white/80 backdrop-blur-[2px]">
                          <h3 className="font-bold text-gray-900 text-sm text-center">Reimagining your artwork</h3>
                          <p className="text-[9px] text-gray-600 mt-1 leading-snug text-center whitespace-normal break-words">
                            Tell Artmosphere what to change and see your artwork transform.
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Step 1: Select artwork then "Let's learn and play" */
              <>
                <div className="flex items-center justify-between p-4 border-b border-amber-100">
                  <h2 id="studio-picker-title" className="text-lg font-bold text-gray-800">
                    {studioPickerCopy?.pickerTitle || 'Select an artwork'}
                  </h2>
                  <button
                    type="button"
                    onClick={() => { closeStudioPicker(); }}
                    className="p-2 rounded-full hover:bg-amber-100 text-gray-600"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4 flex-1 min-h-0 overflow-auto">
                  {studioArtworksLoading ? (
                    <div className="py-12 text-center text-gray-600">Loading your artworks...</div>
                  ) : studioArtworks.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-gray-700 mb-4">You don&apos;t have any artwork yet. Upload one to get started!</p>
                      <button
                        type="button"
                        onClick={() => {
                          closeStudioPicker();
                          setShowArtistWallUploadOverlay(true);
                        }}
                        className="px-6 py-3 rounded-xl font-semibold text-gray-900"
                        style={{
                          background: 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)',
                          boxShadow: '0 4px 14px rgba(255, 193, 7, 0.35)',
                        }}
                      >
                        Upload artwork
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        {studioPickerMode
                          ? 'Choose an artwork to get started:'
                          : 'Choose an artwork, then pick how you want to play:'}
                      </p>
                      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin">
                        {studioArtworks.map((art) => (
                          <button
                            type="button"
                            key={art.id}
                            onClick={() => setSelectedStudioArtwork(selectedStudioArtwork?.id === art.id ? null : art)}
                            className={`flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden border-2 snap-center transition-all ${
                              selectedStudioArtwork?.id === art.id ? 'border-amber-500 ring-2 ring-amber-300' : 'border-gray-200 hover:border-amber-300'
                            }`}
                          >
                            <img src={art.imageUrl} alt={art.title || 'Artwork'} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            if (!selectedStudioArtwork) return;
                            if (studioPickerMode) {
                              continueStudioPicker();
                            } else {
                              setShowPlayChoiceCards(true);
                            }
                          }}
                          disabled={!selectedStudioArtwork}
                          className="px-6 py-3 rounded-xl font-semibold text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background: selectedStudioArtwork ? 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)' : '#e5e7eb',
                            boxShadow: selectedStudioArtwork ? '0 4px 14px rgba(255, 193, 7, 0.35)' : 'none',
                          }}
                        >
                          {studioPickerCopy?.continueLabel || "Let's learn and play"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Header: slim premium strip */}
      <header className="dashboard-landing-header relative z-[300] flex-shrink-0 sticky top-0">
        <div className="dashboard-landing-header-inner max-w-7xl mx-auto">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {user && (
              <div className="flex-shrink-0">
                {artistAvatarId ? (
                  <div className="artist-corner-avatar-ring flex h-9 w-9 items-center justify-center rounded-full text-lg md:h-10 md:w-10 md:text-xl">
                    {getAvatarEmoji(artistAvatarId)}
                  </div>
                ) : artistProfilePicture ? (
                  <img
                    src={artistProfilePicture}
                    alt="Profile"
                    className="artist-corner-avatar-ring h-9 w-9 rounded-full object-cover md:h-10 md:w-10"
                  />
                ) : (
                  <div className="artist-corner-avatar-ring flex h-9 w-9 items-center justify-center rounded-full md:h-10 md:w-10">
                    <User className="h-4 w-4 text-[#8b6914]" aria-hidden />
                  </div>
                )}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="dashboard-corner-badge" aria-label="You are in Artist Corner">
                  Artist Corner
                </span>
                {artistDisplayName ? (
                  <span
                    className="artist-corner-display-name truncate max-w-[10rem] sm:max-w-xs"
                    title={artistDisplayName}
                  >
                    {artistDisplayName}
                  </span>
                ) : null}
              </div>
              <h1 className="artist-corner-hero-title mt-0.5 text-base leading-snug tracking-tight md:text-lg">
                What do you want to do today?
              </h1>
              <p className="artist-corner-hero-sub mt-0.5 line-clamp-1 text-[11px] leading-tight md:text-xs">
                Pick a card to start your creative adventure.
              </p>
            </div>
          </div>
          <nav className="w-full lg:w-auto shrink-0" aria-label="Site navigation">
            <div className="dashboard-landing-nav justify-start sm:justify-end">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="dashboard-nav-pill"
              title="Home"
            >
              <Home aria-hidden />
              <span className="hidden sm:inline">Home</span>
            </button>
            <div className="relative dashboard-account-dropdown">
              <button
                ref={accountBtnRef}
                type="button"
                onClick={() => setShowAccountDropdown((p) => !p)}
                className="dashboard-nav-pill"
                title="My Account"
                aria-expanded={showAccountDropdown}
                aria-haspopup="menu"
              >
                <User aria-hidden />
                <span className="hidden sm:inline">Account</span>
                <span className="text-[#8b6914] text-[10px] leading-none" aria-hidden>⌄</span>
              </button>
            </div>
            {user && <ReactionNotifications variant="nav-pill" />}
            </div>
          </nav>
        </div>
      </header>

      {showAccountDropdown &&
        accountMenuAnchor &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-dashboard-account-menu
            role="menu"
            className="artist-corner-account-menu fixed z-[20060] overflow-hidden rounded-xl py-2"
            style={{
              top: accountMenuAnchor.top,
              right: accountMenuAnchor.right,
              minWidth: accountMenuAnchor.minWidth,
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setShowAccountSettings(true);
                setShowAccountDropdown(false);
              }}
              className="block w-full px-4 py-2 text-left text-sm text-[#2f251b] transition"
            >
              Account Settings
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setShowManageSubscription(true);
                setShowAccountDropdown(false);
              }}
              className="block w-full px-4 py-2 text-left text-sm text-[#2f251b] transition"
            >
              Manage Subscription
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                handleSignOut();
                setShowAccountDropdown(false);
              }}
              className="block w-full px-4 py-2 text-left text-sm text-[#2f251b] transition"
            >
              Sign Out
            </button>
          </div>,
          document.body
        )}

      {/* Explore / search artists — quick access from Artist Corner */}
      <div className="relative z-[200] max-w-7xl mx-auto w-full overflow-visible px-4 md:px-10 pt-3 pb-1">
        <div
          className="artist-corner-search-strip flex flex-col gap-2.5 overflow-visible px-3 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-4"
          role="search"
          aria-label="Search and explore artists"
        >
          <ArtistSearchCombobox
            value={artistSearchQuery}
            onChange={setArtistSearchQuery}
            onExploreAll={goExploreArtists}
          />
          <button
            type="button"
            onClick={goExploreArtists}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full art-btn-primary px-4 py-2 text-sm font-semibold shadow-md transition hover:-translate-y-0.5"
          >
            <Users className="h-4 w-4" aria-hidden />
            Explore Artists
          </button>
        </div>
      </div>

      {/* Main: overflow-visible so shadows not clipped; z below search dropdown */}
      <main ref={mainRef} className="relative z-10 flex-1 flex flex-col min-h-0 max-w-7xl mx-auto w-full px-4 md:px-10 py-5 md:py-7 overflow-visible">
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-stretch min-h-0 content-start overflow-visible">
          {/* Left: two hero cards — Learn Art History (top) + Daily Activity (bottom) */}
          <div className="dashboard-hero-column w-full flex flex-col gap-6 lg:gap-7 min-h-[480px] lg:min-h-[calc(100vh-12rem)] dashboard-big-card overflow-visible">
            <button
              type="button"
              onClick={() => handleEnter('learn-art-history')}
              className="hero-activity-card artist-corner-hero-card w-full rounded-3xl flex-1 min-h-[300px] md:min-h-[280px] lg:min-h-[320px] overflow-hidden relative text-left"
              aria-label="Open Learn Art History"
            >
              <div className="absolute inset-0 rounded-3xl overflow-hidden">
                <ImpastoPastelThumb variant="learn-art-history" />
                {journeyHeroDisplayedSlide?.url ? (
                  <img
                    key={journeyHeroDisplayedSlide.id}
                    src={journeyHeroDisplayedSlide.url}
                    alt=""
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    onError={() => {
                      const failedId = journeyHeroDisplayedSlide.id;
                      failedJourneySlideIdsRef.current.add(failedId);
                      journeyHeroDisplayedRef.current = null;
                      setJourneyHeroDisplayedSlide(null);
                      const slides = journeySlidesRef.current;
                      if (!slides.length) return;
                      const hasValid = slides.some((s) => !failedJourneySlideIdsRef.current.has(s.id));
                      if (hasValid) {
                        setJourneySlideIndex((i) => (i + 1) % slides.length);
                      }
                    }}
                  />
                ) : null}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#fff9ef]/20 via-transparent to-[#f5e8d4]/25" />
              </div>
              <div className="dashboard-landing-hero-pattern absolute inset-0 rounded-3xl" aria-hidden="true" />
              <div className="artist-corner-hero-accent absolute top-0 left-0 right-0 z-10 rounded-t-3xl" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-3xl flex flex-col justify-end z-[9] pointer-events-none">
                <div className="p-5 md:p-6">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-1 drop-shadow-md">Learn Art History</h2>
                  <p className="text-white/95 text-sm md:text-base drop-shadow">Step into stories behind masterpieces and explore galleries by level.</p>
                </div>
              </div>
            </button>

            <div className="hero-activity-card artist-corner-hero-card w-full rounded-3xl flex-1 min-h-[300px] md:min-h-[280px] lg:min-h-[320px] overflow-hidden relative">
              {/* Hero image: place hero.jpg in public/my-art-world/; gradient if missing or load fails */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#fff9ef] via-[#fff5e8]/90 to-[#f5e8d4]/70" />
                {DAILY_ACTIVITY_IMAGE_PATH && !heroImageFailed && (
                  <img
                    src={DAILY_ACTIVITY_IMAGE_PATH}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover rounded-3xl transition-opacity duration-500"
                    onError={() => setHeroImageFailed(true)}
                  />
                )}
              </div>
              <div className="dashboard-landing-hero-pattern absolute inset-0 rounded-3xl" aria-hidden="true" />
              {/* Top accent bar */}
              <div className="artist-corner-hero-accent absolute top-0 left-0 right-0 z-10 rounded-t-3xl" aria-hidden="true" />
              {/* Corner badge */}
              <div className="artist-corner-today-badge absolute top-3 right-3 z-10 rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm">
                Today
              </div>
              {/* Try now! button: amber, 3cm lower, bold; swivel on hover, press on click (see .try-now-hero-btn) */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <button
                  type="button"
                  onClick={handleTryNow}
                  className="try-now-hero-btn pointer-events-auto cursor-pointer rounded-xl px-7 py-3.5 text-lg font-bold tracking-wide whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#f5cb5c]/50 focus:ring-offset-2 md:text-xl"
                  style={{
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                  }}
                  aria-label="Try now"
                >
                  Try now!
                </button>
              </div>
              {/* Text overlaid on image (gradient for readability) */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-3xl flex flex-col justify-end z-[9] pointer-events-none">
                <div className="p-5 md:p-6">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-1 drop-shadow-md">Art Activity of the Day</h2>
                  <p className="text-white/95 text-sm md:text-base drop-shadow">Try today&apos;s 5-minute creative challenge.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: all non-hero cards */}
          <div className="artm-grid-cards artm-feature-cards-grid gap-4 sm:gap-6 md:gap-7 content-start overflow-visible">
            {nonHeroFeatures.map((f) => {
              return (
              <FlipCard
                key={f.key}
                featureKey={f.key}
                title={f.title}
                short={f.short}
                long={f.long}
                bullets={f.bullets}
                imageUrl={f.imageUrl || ''}
                impastoVariant={f.impastoVariant}
                isFlipped={openCardKey === f.key}
                onFlip={setOpenCardKey}
                onEnter={handleEnter}
                className="flip-card-dashboard w-full min-h-0 aspect-[3/2] sm:min-h-[200px] md:min-h-[240px]"
              />
              );
            })}
          </div>
        </div>
      </main>

      {/* Account Settings and Manage Subscription modals (same as navbar) */}
      <AccountSettingsModal isOpen={showAccountSettings} onClose={() => setShowAccountSettings(false)} />
      <ManageSubscriptionModal isOpen={showManageSubscription} onClose={() => setShowManageSubscription(false)} />
    </div>
  );
};

export default DashboardLanding;
